// Display formatting (architecture sprint-02 §10.0). Locale en-GB everywhere; durations come from
// the shared module so server and client agree. Never format dates ad hoc in components.
import type { LocalDate } from '@shared/calendar';
export { formatDurationHm } from '@shared/duration';
export { formatHours2 } from '@shared/hours';

const LOCALE = 'en-GB';
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

const localDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** '2026-10-07' → 'Wed 7 Oct 2026'. Parsed as UTC so the calendar date never shifts. */
export function formatLocalDate(date: LocalDate): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return localDateFormatter.format(parsed).replace(/,/g, '');
}

/** ISO instant → '7 Oct 2026, 14:05' in the viewer's browser zone. */
export function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const date = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }).format(parsed);
  const time = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(parsed);
  return `${date}, ${time}`;
}

/** Seconds since local midnight → '09:00'. */
export function formatClock(second: number): string {
  const hours = Math.floor(second / SECONDS_PER_HOUR) % 24;
  const minutes = Math.floor((second % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

const shortLocalDateFormatter = new Intl.DateTimeFormat(LOCALE, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

/** '2026-10-07' → 'Wed 7 Oct' (overnight hint in the entry form). */
export function formatShortLocalDate(date: LocalDate): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return shortLocalDateFormatter.format(parsed).replace(/,/g, '');
}

const dayMonthYear = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function dateParts(date: LocalDate): { day: number; month: string; year: number } | null {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = dayMonthYear.formatToParts(parsed);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return { day: Number(get('day')), month: get('month'), year: Number(get('year')) };
}

/**
 * Week heading (sprint 4 §6.2): '5 – 11 Oct 2026', '28 Sep – 4 Oct 2026', '29 Dec 2025 – 4 Jan 2026'.
 */
export function formatWeekHeading(start: LocalDate, end: LocalDate): string {
  const a = dateParts(start);
  const b = dateParts(end);
  if (!a || !b) return `${start} – ${end}`;
  if (a.year !== b.year) return `${a.day} ${a.month} ${a.year} – ${b.day} ${b.month} ${b.year}`;
  if (a.month !== b.month) return `${a.day} ${a.month} – ${b.day} ${b.month} ${b.year}`;
  return `${a.day} – ${b.day} ${b.month} ${b.year}`;
}

const shortDayMonth = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** '2026-10-05' → '5 Oct' (chart axes, week labels). */
export function formatDayMonth(date: LocalDate): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return shortDayMonth.format(parsed);
}

/**
 * Range heading (sprint 5 §6.4): '1 Oct – 31 Oct 2026', '28 Dec 2026 – 3 Jan 2027', one day '7 Oct 2026'.
 */
export function formatRange(from: LocalDate, to: LocalDate): string {
  const a = dateParts(from);
  const b = dateParts(to);
  if (!a || !b) return `${from} – ${to}`;
  if (from === to) return `${b.day} ${b.month} ${b.year}`;
  if (a.year !== b.year) return `${a.day} ${a.month} ${a.year} – ${b.day} ${b.month} ${b.year}`;
  return `${a.day} ${a.month} – ${b.day} ${b.month} ${b.year}`;
}
